import { useEffect, useRef, useState } from 'react';

export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions<T> {
  /** The current value to save. Save fires when this changes. */
  value: T;
  /** The save function. Called with the latest value. */
  save: (value: T) => Promise<void>;
  /** Debounce window in ms before save fires after the last change. */
  delay?: number;
  /** Skip the very first effect run (on mount) so we don't save the initial value. */
  skipInitial?: boolean;
}

/**
 * Debounced autosave hook.
 *
 * Behavior:
 *   - Save fires `delay` ms after the last change to `value`.
 *   - Reference equality matters: pass a stable / memoized `value` so the timer
 *     resets only when the value really changes, not on every parent render.
 *   - On unmount: cancels any pending debounce timer AND, if there are
 *     uncommitted changes, fires one last save (fire-and-forget) so navigating
 *     away within the debounce window doesn't lose data.
 *   - After unmount, no React state is touched (no warnings).
 */
export function useAutoSave<T>({
  value,
  save,
  delay = 500,
  skipInitial = true,
}: UseAutoSaveOptions<T>): SaveState {
  const [state, setState] = useState<SaveState>('idle');
  const initialRef = useRef(skipInitial);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef(value);
  const inflightRef = useRef(false);
  const pendingRef = useRef(false);
  const dirtyRef = useRef(false);
  const disposedRef = useRef(false);

  // Always keep the latest value and save callback in refs so timer-fired
  // saves and the unmount-flush both grab the freshest references regardless
  // of when they were originally scheduled.
  latestValueRef.current = value;
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }

    dirtyRef.current = true;
    if (!disposedRef.current) setState('pending');
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void runSave();
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Unmount-only effect: cancel pending timer and flush uncommitted changes
  // by firing one final save.
  //
  // StrictMode caveat: in dev React simulates an unmount/remount on first
  // mount by running effects twice (setup → cleanup → setup). useRef values
  // persist across that cycle, so without resetting it `disposedRef` would
  // stay `true` after the simulated unmount and silently no-op every
  // subsequent timer-driven save. Reset it at the top of setup.
  useEffect(() => {
    disposedRef.current = false;
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      disposedRef.current = true;

      // If there are unsaved changes, fire one last save with the latest
      // value. We deliberately flush even when a save is currently in
      // flight: that older save was started with an earlier snapshot and
      // may not include what the user just typed. Both calls hit mockApi
      // synchronously, so last-write-wins gives us the latest value.
      if (dirtyRef.current) {
        void saveRef.current(latestValueRef.current).catch((err) => {
          console.error('autosave flush on unmount failed', err);
        });
      }
    };
  }, []);

  async function runSave() {
    if (disposedRef.current) return;
    if (inflightRef.current) {
      // A save is already running — flag a pending re-save when it finishes
      pendingRef.current = true;
      return;
    }
    inflightRef.current = true;
    if (!disposedRef.current) setState('saving');
    try {
      await saveRef.current(latestValueRef.current);
      // dirtyRef is reset before the post-save check so a re-fire from
      // pendingRef accurately reflects edits made *during* the save.
      dirtyRef.current = false;
      if (!disposedRef.current) setState('saved');
    } catch (err) {
      console.error('autosave failed', err);
      if (!disposedRef.current) setState('error');
    } finally {
      inflightRef.current = false;
      if (pendingRef.current && !disposedRef.current) {
        pendingRef.current = false;
        await runSave();
      }
    }
  }

  return state;
}
